/**
 * AnalyticsDashboard Component
 * Admin activity analytics — accurate period metrics + Lift AI–style layout.
 */

import React, { useMemo } from 'react';
import {
  Activity,
  Users,
  AlertCircle,
  CheckCircle2,
  Clock,
  BarChart3,
  TrendingUp,
  Zap,
  Target,
  ShieldAlert,
  PencilLine,
  PlusCircle,
  Trash2,
  MoreHorizontal,
  CalendarDays,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { DynamicCard, type TrendConfig } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  AdminLogEntry,
  CATEGORY_CONFIGS,
  SEVERITY_CONFIGS,
} from '../../types';
import {
  AnalyticsDateRange,
  generateAnalytics,
} from '../../utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CHART_COLORS = {
  primary: '#334238',
  success: '#22c55e',
  danger: '#ef4444',
  warning: '#f59e0b',
  muted: '#585755',
  liftGreen: '#E3FF6A',
  grid: '#E5E1DF',
};

const STABLE_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'index' as const, intersect: false },
};

interface AnalyticsDashboardProps {
  /** Unfiltered loaded logs — not Activity-tab list filters. */
  logs: AdminLogEntry[];
  /** Shared page date range (same control as Activity Logs). */
  dateRange: AnalyticsDateRange;
  isLoading?: boolean;
}

function toTrendConfig(
  trend?: { direction: 'up' | 'down' | 'neutral'; value: string },
  successWhenUp = true
): TrendConfig | undefined {
  if (!trend) return undefined;
  const colorScheme =
    trend.direction === 'neutral'
      ? 'default'
      : trend.direction === 'up'
        ? successWhenUp
          ? 'success'
          : 'destructive'
        : successWhenUp
          ? 'destructive'
          : 'success';
  return {
    direction: trend.direction,
    value: trend.value,
    showIcon: true,
    colorScheme,
  };
}

function formatHour(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function formatCount(value: number): string {
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

export function AnalyticsDashboard({
  logs,
  dateRange,
  isLoading,
}: AnalyticsDashboardProps) {
  const analytics = useMemo(() => {
    // Always compute against the shared date window (even if empty → empty state)
    return generateAnalytics(logs, dateRange);
  }, [logs, dateRange]);

  const activityTrendChart = useMemo(() => {
    if (!analytics?.activityByDay?.length) return null;
    return {
      labels: analytics.activityByDay.map((d) => {
        const [y, m, day] = d.date.split('-').map(Number);
        const date = new Date(y, m - 1, day);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
      }),
      datasets: [
        {
          label: 'Actions',
          data: analytics.activityByDay.map((d) => d.count),
          borderColor: CHART_COLORS.primary,
          backgroundColor: `${CHART_COLORS.primary}22`,
          borderWidth: 3,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'Errors',
          data: analytics.activityByDay.map((d) => d.errors),
          borderColor: CHART_COLORS.danger,
          backgroundColor: `${CHART_COLORS.danger}18`,
          borderWidth: 2,
          fill: true,
          tension: 0.35,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [analytics]);

  const hourlyChart = useMemo(() => {
    if (!analytics?.activityByHour?.length) return null;
    const max = Math.max(...analytics.activityByHour.map((h) => h.count), 1);
    return {
      labels: analytics.activityByHour.map((h) => formatHour(h.hour)),
      datasets: [
        {
          label: 'Actions',
          data: analytics.activityByHour.map((h) => h.count),
          backgroundColor: analytics.activityByHour.map((h) => {
            const intensity = h.count / max;
            const alpha = Math.round(40 + intensity * 180)
              .toString(16)
              .padStart(2, '0');
            return `${CHART_COLORS.primary}${alpha}`;
          }),
          borderColor: CHART_COLORS.primary,
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [analytics]);

  const categoryChart = useMemo(() => {
    if (!analytics?.actionBreakdown?.length) return null;
    const top = analytics.actionBreakdown.slice(0, 8);
    return {
      labels: top.map(
        (item) =>
          CATEGORY_CONFIGS.find((c) => c.id === item.category)?.label ||
          item.category
      ),
      datasets: [
        {
          label: 'Actions',
          data: top.map((item) => item.count),
          backgroundColor: CHART_COLORS.liftGreen,
          borderColor: CHART_COLORS.primary,
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [analytics]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-1">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-28 rounded-xl bg-gray-100 animate-pulse"
            />
          ))}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="h-72 rounded-xl bg-gray-100 animate-pulse" />
          <div className="h-72 rounded-xl bg-gray-100 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!analytics || analytics.totalActions === 0) {
    return (
      <div className="space-y-4">
        <div className="text-left">
          <h3 className="text-lg font-semibold text-[#242424]">
            Admin Activity Analytics
          </h3>
          <p className="text-sm text-[#585755] flex items-center gap-1.5 mt-0.5">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            {analytics?.periodLabel || 'Selected date range'}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <BarChart3 className="h-10 w-10 text-muted-foreground mb-3" />
          <h3 className="text-lg font-semibold text-[#242424]">
            No analytics data yet
          </h3>
          <p className="text-sm text-[#585755] max-w-md mt-1">
            {analytics?.coverageNote ||
              'Change the date range in the header or refresh to load activity for this window.'}
          </p>
        </div>
      </div>
    );
  }

  const kpiCardClassName =
    'min-w-0 w-full max-w-full h-full border border-[rgba(46,43,41,0.12)] shadow-none [&_[data-slot=card-header]]:px-3 [&_[data-slot=card-header]]:py-2.5 [&_[data-slot=card-content]]:px-3 [&_[data-slot=card-content]]:py-2.5 [&_[data-slot=card-content]_p]:text-xs [&_[data-slot=card-content]_p]:leading-snug [&_[data-slot=card-header]>div]:min-w-0 [&_[data-slot=card-header]_div]:truncate [&_[data-slot=card-header]_div]:text-sm';

  const mutationTotal =
    analytics.mutations.creates +
    analytics.mutations.updates +
    analytics.mutations.deletes +
    analytics.mutations.other;

  return (
    <div className="space-y-5 w-full min-w-0 pb-4">
      <div className="flex items-start justify-between gap-4 flex-wrap min-w-0">
        <div className="text-left min-w-0">
          <h3 className="text-lg font-semibold text-[#242424]">
            Admin Activity Analytics
          </h3>
          <p className="text-sm text-[#585755] flex items-center gap-1.5 mt-0.5">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            {analytics.periodLabel}
          </p>
          {analytics.coverageNote && (
            <p
              className={cn(
                'text-xs mt-1',
                analytics.coverageIncomplete
                  ? 'text-amber-700'
                  : 'text-[#585755]'
              )}
            >
              {analytics.coverageNote}
            </p>
          )}
        </div>
      </div>

      <div className="grid w-full min-w-0 gap-3 grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 [&>*]:min-w-0">
        <DynamicCard
          title="Total Actions"
          value={formatCount(analytics.totalActions)}
          valueType="text"
          trend={toTrendConfig(analytics.trends.totalActions)}
          description={`Across ${analytics.daySpan} calendar day${analytics.daySpan === 1 ? '' : 's'}`}
          shadow="none"
          size="sm"
          headerIcon={<Activity className="h-4 w-4 text-[#334238]" />}
          showMoreIcon={false}
          className={kpiCardClassName}
        />
        <DynamicCard
          title="Success Rate"
          value={`${analytics.successRate}%`}
          valueType="text"
          trend={toTrendConfig(analytics.trends.successRate)}
          description={`${formatCount(analytics.successCount)} successful`}
          shadow="none"
          size="sm"
          headerIcon={<CheckCircle2 className="h-4 w-4 text-[#334238]" />}
          showMoreIcon={false}
          className={kpiCardClassName}
        />
        <DynamicCard
          title="Error Rate"
          value={`${analytics.errorRate}%`}
          valueType="text"
          trend={toTrendConfig(analytics.trends.errorRate, false)}
          description={`${formatCount(analytics.errorCount)} failed actions`}
          shadow="none"
          size="sm"
          headerIcon={<AlertCircle className="h-4 w-4 text-[#334238]" />}
          showMoreIcon={false}
          className={kpiCardClassName}
        />
        <DynamicCard
          title="Active Admins"
          value={formatCount(analytics.activeUsers)}
          valueType="text"
          trend={toTrendConfig(analytics.trends.activeUsers)}
          description={`${formatCount(analytics.actionsPerAdmin)} actions / admin`}
          shadow="none"
          size="sm"
          headerIcon={<Users className="h-4 w-4 text-[#334238]" />}
          showMoreIcon={false}
          className={kpiCardClassName}
        />
        <DynamicCard
          title="Avg / Day"
          value={formatCount(analytics.avgActionsPerDay)}
          valueType="text"
          description={`Total ÷ ${analytics.daySpan} days in period`}
          shadow="none"
          size="sm"
          headerIcon={<TrendingUp className="h-4 w-4 text-[#334238]" />}
          showMoreIcon={false}
          className={kpiCardClassName}
        />
        <DynamicCard
          title="Peak Hour"
          value={
            analytics.peakHourCount > 0
              ? formatHour(analytics.peakHour)
              : '—'
          }
          valueType="text"
          description={
            analytics.peakHourCount > 0
              ? `${formatCount(analytics.peakHourCount)} actions at peak`
              : 'No peak in period'
          }
          shadow="none"
          size="sm"
          headerIcon={<Zap className="h-4 w-4 text-[#334238]" />}
          showMoreIcon={false}
          className={kpiCardClassName}
        />
      </div>

      {/* Mutations performed */}
      <div className="grid gap-3 min-w-0 [grid-template-columns:repeat(2,minmax(0,1fr))] md:[grid-template-columns:repeat(4,minmax(0,1fr))]">
        {(
          [
            {
              key: 'creates',
              label: 'Creates',
              value: analytics.mutations.creates,
              icon: PlusCircle,
            },
            {
              key: 'updates',
              label: 'Updates',
              value: analytics.mutations.updates,
              icon: PencilLine,
            },
            {
              key: 'deletes',
              label: 'Deletes',
              value: analytics.mutations.deletes,
              icon: Trash2,
            },
            {
              key: 'other',
              label: 'Other actions',
              value: analytics.mutations.other,
              icon: MoreHorizontal,
            },
          ] as const
        ).map((item) => {
          const Icon = item.icon;
          const pct =
            mutationTotal > 0
              ? Math.round((item.value / mutationTotal) * 1000) / 10
              : 0;
          return (
            <div
              key={item.key}
              className="min-w-0 rounded-xl border border-[#E5E1DF] bg-white px-3 py-3"
            >
              <div className="flex items-center gap-2 text-[#585755] mb-1">
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="text-xs font-medium">{item.label}</span>
              </div>
              <div className="text-2xl font-bold text-[#242424]">
                {formatCount(item.value)}
              </div>
              <div className="text-[11px] text-[#585755]">{pct}% of actions</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-3 w-full min-w-0 xl:[grid-template-columns:repeat(5,minmax(0,1fr))]">
        <div className="xl:col-span-3 min-w-0 overflow-hidden bg-white rounded-xl border border-[#E5E1DF] p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-[#585755] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#242424]">
                Activity & Errors Over Time
              </div>
              <div className="text-xs text-[#585755]">
                Daily admin actions vs failed operations (local time)
              </div>
            </div>
          </div>
          <div className="relative h-64 sm:h-72 w-full min-w-0 overflow-hidden">
            {activityTrendChart ? (
              <Line
                data={activityTrendChart}
                options={{
                  ...STABLE_CHART_OPTIONS,
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: CHART_COLORS.muted,
                        precision: 0,
                      },
                      grid: { color: CHART_COLORS.grid },
                    },
                    x: {
                      grid: { display: false },
                      ticks: {
                        color: CHART_COLORS.muted,
                        maxTicksLimit: analytics.daySpan <= 7 ? 7 : 12,
                      },
                    },
                  },
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        boxWidth: 6,
                        color: CHART_COLORS.muted,
                      },
                    },
                    tooltip: {
                      backgroundColor: 'rgba(51, 66, 56, 0.95)',
                      titleColor: 'white',
                      bodyColor: 'white',
                      borderColor: CHART_COLORS.primary,
                      borderWidth: 1,
                      cornerRadius: 8,
                    },
                  },
                }}
              />
            ) : null}
          </div>
        </div>

        <div className="xl:col-span-2 min-w-0 overflow-hidden bg-white rounded-xl border border-[#E5E1DF] p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-[#585755] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#242424]">
                24-Hour Usage Pattern
              </div>
              <div className="text-xs text-[#585755]">
                When admins are most active (local time)
              </div>
            </div>
          </div>
          <div className="relative h-64 sm:h-72 w-full min-w-0 overflow-hidden">
            {hourlyChart ? (
              <Bar
                data={hourlyChart}
                options={{
                  ...STABLE_CHART_OPTIONS,
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: CHART_COLORS.muted,
                        precision: 0,
                      },
                      grid: { color: CHART_COLORS.grid },
                    },
                    x: {
                      grid: { display: false },
                      ticks: {
                        color: CHART_COLORS.muted,
                        maxTicksLimit: 12,
                        maxRotation: 0,
                      },
                    },
                  },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'rgba(51, 66, 56, 0.95)',
                      titleColor: 'white',
                      bodyColor: 'white',
                      cornerRadius: 8,
                      callbacks: {
                        afterBody: (items) => {
                          const idx = items[0]?.dataIndex;
                          if (idx == null) return '';
                          const row = analytics.activityByHour[idx];
                          return row ? `Errors: ${row.errors}` : '';
                        },
                      },
                    },
                  },
                }}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 w-full min-w-0 xl:[grid-template-columns:repeat(3,minmax(0,1fr))]">
        <div className="min-w-0 overflow-hidden bg-white rounded-xl border border-[#E5E1DF] p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-[#585755] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#242424]">
                Actions by Category
              </div>
              <div className="text-xs text-[#585755]">
                Where admin effort is concentrated
              </div>
            </div>
          </div>
          <div className="relative h-64 w-full min-w-0 overflow-hidden">
            {categoryChart ? (
              <Bar
                data={categoryChart}
                options={{
                  ...STABLE_CHART_OPTIONS,
                  indexAxis: 'y' as const,
                  scales: {
                    x: {
                      beginAtZero: true,
                      ticks: {
                        color: CHART_COLORS.muted,
                        precision: 0,
                      },
                      grid: { color: CHART_COLORS.grid },
                    },
                    y: {
                      grid: { display: false },
                      ticks: { color: CHART_COLORS.muted },
                    },
                  },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'rgba(51, 66, 56, 0.95)',
                      titleColor: 'white',
                      bodyColor: 'white',
                      cornerRadius: 8,
                      callbacks: {
                        afterLabel: (ctx) => {
                          const row = analytics.actionBreakdown[ctx.dataIndex];
                          return row ? `${row.percentage}% of period` : '';
                        },
                      },
                    },
                  },
                }}
              />
            ) : (
              <p className="text-sm text-[#585755] py-8 text-center">
                No category data
              </p>
            )}
          </div>
        </div>

        <div className="min-w-0 bg-white rounded-xl border border-[#E5E1DF] p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-4 w-4 text-[#585755] shrink-0" />
            <div>
              <div className="text-sm font-semibold text-[#242424]">
                Top Actions
              </div>
              <div className="text-xs text-[#585755]">
                Most frequent admin operations
              </div>
            </div>
          </div>
          <div className="space-y-2.5 max-h-64 overflow-auto pr-1">
            {analytics.topActions.map((item, i) => (
              <div key={`${item.action}-${i}`} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#F7F4F2] text-xs font-medium text-[#585755] flex items-center justify-center shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-medium text-[#242424] truncate"
                    title={item.action}
                  >
                    {item.action}
                  </div>
                  <div className="h-1.5 mt-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#CCE561] rounded-full"
                      style={{
                        width: `${Math.min(100, Math.max(item.percentage, 2))}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-[#242424]">
                    {item.count}
                  </div>
                  <div className="text-[10px] text-[#585755]">
                    {item.percentage}%
                  </div>
                </div>
              </div>
            ))}
            {!analytics.topActions.length && (
              <p className="text-sm text-[#585755]">No action types found.</p>
            )}
          </div>
        </div>

        <div className="min-w-0 bg-white rounded-xl border border-[#E5E1DF] p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-[#585755] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#242424]">
                Most Active Admins
              </div>
              <div className="text-xs text-[#585755]">
                Volume and error contribution
              </div>
            </div>
          </div>
          <div className="space-y-2.5 max-h-64 overflow-auto pr-1">
            {analytics.topUsers.map((user, i) => {
              const errPct =
                user.actionCount > 0
                  ? Math.round((user.errorCount / user.actionCount) * 1000) / 10
                  : 0;
              return (
                <div
                  key={`${user.userId}-${user.userEmail}-${i}`}
                  className="flex items-center gap-3 py-1"
                >
                  <div className="w-6 h-6 rounded-full bg-[#F7F4F2] text-xs font-medium text-[#585755] flex items-center justify-center shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#242424] truncate">
                      {user.userName}
                    </div>
                    <div className="text-[11px] text-[#585755] truncate">
                      {user.userEmail}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-[#242424]">
                      {user.actionCount}
                    </div>
                    <div
                      className={cn(
                        'text-[10px]',
                        errPct > 0 ? 'text-red-600' : 'text-[#585755]'
                      )}
                    >
                      {user.errorCount} err · {errPct}%
                    </div>
                  </div>
                </div>
              );
            })}
            {!analytics.topUsers.length && (
              <p className="text-sm text-[#585755]">No admin activity found.</p>
            )}
          </div>
        </div>
      </div>

      <div className="min-w-0 w-full bg-white rounded-xl border border-[#E5E1DF] p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="h-4 w-4 text-[#585755] shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#242424]">
              Result Health
            </div>
            <div className="text-xs text-[#585755]">
              Severity mix across the selected period
            </div>
          </div>
        </div>
        <div className="grid gap-3 min-w-0 [grid-template-columns:repeat(2,minmax(0,1fr))] md:[grid-template-columns:repeat(4,minmax(0,1fr))]">
          {(['INFO', 'WARNING', 'ERROR', 'ALERT'] as const).map((severity) => {
            const row = analytics.severityBreakdown.find(
              (s) => s.severity === severity
            );
            const config = SEVERITY_CONFIGS[severity];
            return (
              <div
                key={severity}
                className={cn(
                  'rounded-lg border px-3 py-3',
                  config.bgColor,
                  'border-transparent'
                )}
              >
                <div className={cn('text-xs font-medium mb-1', config.color)}>
                  {config.label}
                </div>
                <div className="text-2xl font-bold text-[#242424]">
                  {row?.count ?? 0}
                </div>
                <div className="text-[11px] text-[#585755]">
                  {row?.percentage ?? 0}% of actions
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
